import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import HeaderCard from "@/design/components/HeaderCard";
import FormGroup from "@/design/forms/FormGroup";
import { setLogRefreshInterval, toggleLogExpand } from "@/store/reducers/settings";
import Valkyrie, { viAngleDown, viClock, viExpand } from "@sippy-platform/valkyrie";

import { Box, Grid, List, ListItemIcon, ListItemText, Menu, MenuItem, Stack, Switch, Typography } from "@mui/material";

import SettingsOption from "./components/SettingsOption";

export default function Logs() {
  const dispatch = useDispatch();

  const logSettings = useSelector((state) => state.settings.logs);

  // Refresh menu
  const [refreshAnchorEl, setRefreshAnchorEl] = useState(null);
  const refreshMenuOpen = Boolean(refreshAnchorEl);
  const openRefreshMenu = (event) => {
    setRefreshAnchorEl(event.currentTarget);
  };
  const closeRefreshMenu = (refreshInterval) => {
    if (refreshInterval !== undefined) dispatch(setLogRefreshInterval(refreshInterval));
    setRefreshAnchorEl(null);
  };

  return (
    <Stack spacing={2}>
      <HeaderCard title="Logs & statistics" sx={{ ".MuiCardHeader-title": { my: 0 } }} />
      {logSettings && (
        <>
          <FormGroup title="General">
            <Grid size={12}>
              <List>
                <SettingsOption onClick={openRefreshMenu}>
                  <ListItemIcon>
                    <Valkyrie icon={viClock} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Refresh interval"
                    secondary="Time before the logs and statistics automatically refresh the data."
                  />
                  <Stack
                    direction="row"
                    sx={{
                      alignItems: "center",
                      gap: 0.5,
                      pl: 1,
                    }}
                  >
                    <Typography variant="body2" sx={{ m: 0 }}>
                      {logSettings.refreshInterval
                        ? `${logSettings.refreshInterval} minute${logSettings.refreshInterval >= 2 ? "s" : ""}`
                        : "Don't refresh"}
                    </Typography>
                    <Valkyrie icon={viAngleDown} />
                  </Stack>
                </SettingsOption>
              </List>
              <Menu
                anchorEl={refreshAnchorEl}
                open={refreshMenuOpen}
                onClose={() => closeRefreshMenu()}
                slotProps={{
                  paper: {
                    elevation: 1,
                    sx: {
                      mt: 0.5,
                      border: "1px solid",
                      borderColor: "divider",
                      minWidth: 140,
                    },
                  },
                }}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "right",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
              >
                <MenuItem
                  onClick={() => closeRefreshMenu(0)}
                  selected={logSettings.refreshInterval === 0}
                  sx={{ px: 1.5 }}
                >
                  Don't refresh
                </MenuItem>
                {[1, 2, 5, 10, 15].map((minutes) => (
                  <MenuItem
                    key={minutes}
                    onClick={() => closeRefreshMenu(minutes)}
                    selected={logSettings.refreshInterval === minutes}
                    sx={{ px: 1.5 }}
                  >
                    {minutes} minute{minutes >= 2 ? "s" : ""}
                  </MenuItem>
                ))}
              </Menu>
            </Grid>
          </FormGroup>
          <FormGroup title="View">
            <Grid size={12}>
              <List>
                <SettingsOption onClick={() => dispatch(toggleLogExpand())}>
                  <ListItemIcon>
                    <Valkyrie icon={viExpand} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Expand view"
                    secondary="Allow logs and statistics pages that support it to take up more width."
                  />
                  <Box sx={{ p: 0, justifyContent: "flex-end", pl: 1 }}>
                    <Switch checked={logSettings.expand || false} sx={{ m: 0, mt: -0.5 }} />
                  </Box>
                </SettingsOption>
              </List>
            </Grid>
          </FormGroup>
        </>
      )}
    </Stack>
  );
}
